import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from './ui/button';
import { Input, Label, FieldHint } from './ui/input';
import { WhatsThis } from './ui/tooltip';
import { scopeCheckApi } from '../lib/api-client';
import { friendlyErrorMessage } from './ui/states';
import type { ScopePolicy, ScopeRuleSet, ScopeZone } from '../lib/types';

function emptyRuleSet(): ScopeRuleSet {
  return { cidrs: [], domains: [], hosts: [], ports: [], paths: [] };
}

export function emptyScopePolicy(zone: ScopeZone): ScopePolicy {
  return { zone, allow: emptyRuleSet(), deny: emptyRuleSet(), allowUnlistedPorts: false };
}

function CsvField({
  label,
  hint,
  values,
  onChange,
  placeholder,
}: {
  label: string;
  hint: string;
  values: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
}) {
  const [text, setText] = useState(values.join(', '));
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <Input
        value={text}
        placeholder={placeholder}
        onChange={(e) => {
          setText(e.target.value);
          onChange(
            e.target.value
              .split(',')
              .map((v) => v.trim())
              .filter(Boolean),
          );
        }}
      />
      <FieldHint>{hint}</FieldHint>
    </div>
  );
}

export function ScopePolicyEditor({
  value,
  onChange,
}: {
  value: ScopePolicy;
  onChange: (policy: ScopePolicy) => void;
}) {
  const [checkTarget, setCheckTarget] = useState('');
  const [checkPort, setCheckPort] = useState('');
  const checkMutation = useMutation({
    mutationFn: () =>
      scopeCheckApi.check({
        scopePolicy: value,
        target: { host: checkTarget || undefined, port: checkPort ? Number(checkPort) : undefined },
      }),
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div className="space-y-4 rounded-xl border border-border p-4">
          <p className="text-sm font-semibold text-ink">Allow list</p>
          <CsvField
            label="Domains"
            hint="Comma-separated, e.g. example.com, api.example.com"
            values={value.allow.domains}
            onChange={(domains) => onChange({ ...value, allow: { ...value.allow, domains } })}
            placeholder="example.com"
          />
          <CsvField
            label="Hosts (exact)"
            hint="Exact hostnames only, no wildcards"
            values={value.allow.hosts}
            onChange={(hosts) => onChange({ ...value, allow: { ...value.allow, hosts } })}
            placeholder="host.example.com"
          />
          <CsvField
            label={'IP ranges (CIDR) '}
            hint="e.g. 10.0.0.0/24"
            values={value.allow.cidrs}
            onChange={(cidrs) => onChange({ ...value, allow: { ...value.allow, cidrs } })}
            placeholder="10.0.0.0/24"
          />
          <CsvField
            label="Ports"
            hint="e.g. 443, 8443"
            values={value.allow.ports}
            onChange={(ports) => onChange({ ...value, allow: { ...value.allow, ports } })}
            placeholder="443"
          />
          <CsvField
            label="Paths"
            hint="e.g. /api, /login"
            values={value.allow.paths}
            onChange={(paths) => onChange({ ...value, allow: { ...value.allow, paths } })}
            placeholder="/api"
          />
        </div>
        <div className="space-y-4 rounded-xl border border-border p-4">
          <p className="text-sm font-semibold text-ink">
            Deny list <span className="font-normal text-ink-muted">— always wins over allow</span>
          </p>
          <CsvField
            label="Domains"
            hint="Never test these, even if otherwise allowed"
            values={value.deny.domains}
            onChange={(domains) => onChange({ ...value, deny: { ...value.deny, domains } })}
            placeholder="staging.example.com"
          />
          <CsvField
            label="Hosts (exact)"
            hint=""
            values={value.deny.hosts}
            onChange={(hosts) => onChange({ ...value, deny: { ...value.deny, hosts } })}
            placeholder=""
          />
          <CsvField
            label="IP ranges (CIDR)"
            hint=""
            values={value.deny.cidrs}
            onChange={(cidrs) => onChange({ ...value, deny: { ...value.deny, cidrs } })}
            placeholder=""
          />
          <CsvField
            label="Ports"
            hint=""
            values={value.deny.ports}
            onChange={(ports) => onChange({ ...value, deny: { ...value.deny, ports } })}
            placeholder=""
          />
          <CsvField
            label="Paths"
            hint=""
            values={value.deny.paths}
            onChange={(paths) => onChange({ ...value, deny: { ...value.deny, paths } })}
            placeholder=""
          />
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-ink">
        <input
          type="checkbox"
          checked={value.allowUnlistedPorts}
          onChange={(e) => onChange({ ...value, allowUnlistedPorts: e.target.checked })}
          className="h-4 w-4 rounded border-border"
        />
        Allow ports not explicitly listed above
        <WhatsThis>
          Off by default (recommended). When off, only the ports you list are ever tested — anything else is blocked automatically.
        </WhatsThis>
      </label>

      <div className="rounded-xl bg-surface-sunken p-4">
        <p className="mb-2 text-sm font-semibold text-ink">Try it — check if a target would be allowed</p>
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label htmlFor="check-host">Host</Label>
            <Input id="check-host" value={checkTarget} onChange={(e) => setCheckTarget(e.target.value)} placeholder="app.example.com" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="check-port">Port</Label>
            <Input id="check-port" value={checkPort} onChange={(e) => setCheckPort(e.target.value)} placeholder="443" className="w-24" />
          </div>
          <Button type="button" variant="outline" onClick={() => checkMutation.mutate()} disabled={!checkTarget}>
            Check
          </Button>
        </div>
        {checkMutation.data ? (
          <p className={`mt-2 text-sm font-medium ${checkMutation.data.decision === 'ALLOW' ? 'text-success' : 'text-danger'}`}>
            {checkMutation.data.decision === 'ALLOW' ? 'Allowed' : 'Blocked'} — {checkMutation.data.reason}
          </p>
        ) : null}
        {checkMutation.isError ? <p className="mt-2 text-sm text-danger">{friendlyErrorMessage(checkMutation.error)}</p> : null}
      </div>
    </div>
  );
}
